import React, { useContext, useState, useEffect } from "react";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { Grid, Button } from "@mui/material";
import Drawer from "../../Components/Common/DrawerLayout/Drawer";
import TextFieldRenderer from "../../Components/Form/TextFieldRenderer";
import {
  REGISTER_USER_SA,
  SEARCH_USER_WITH_IDENTITY_SA,
} from "./SuperAdminService";
import { SnackBarContext } from "../../context/SnackBarContext";
import Stack from "@mui/material/Stack";
import { makeStyles } from "@mui/styles";
import { colors } from "../../theme/colors";
import { fonts } from "../../theme/fonts";
import { useTranslation } from "../../context/Internationalization";
import useGraphql from "../../Hooks/useGraphql";
import useLog from "../../Hooks/useLog";
import { getStorage } from "../../auth/utils";
import { USER_ROLES } from "../../utility/Enum";
import {
  DrawerBody,
  DrawerFooter,
  DrawerLayout,
  DrawerSection,
  DrawerTitleHeader,
  getTransactionalDrawerPaperProps,
} from "../../Components/Common/DrawerLayout/DrawerLayout";

const useStyles = makeStyles({
  userIdentityContainer: {
    height: "auto",
    width: "100%",
    margin: "8px 0",
    padding: "8px 16px",
    background: colors.Base.White,
    border: `1px solid ${colors.Grey[300]}`,
    boxShadow: `0px 1px 2px rgba(22, 31, 37, 0.05)`,
    borderRadius: "8px",
  },
  userIdentityName: {
    ...fonts.text.md.normal,
    color: colors.Grey[900],
  },
  userIdentityDetail: {
    ...fonts.text.md.normal,
    color: colors.Grey[500],
  },
});

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeMobileIdentity = (value) => {
  const normalizedMobile = value.replace(/\s/g, "").replace(/\+/g, "");
  const digitsOnly = normalizedMobile.replace(/\D/g, "");

  if (digitsOnly.length === 10 && digitsOnly.startsWith("69")) {
    return `30${digitsOnly}`;
  }

  if (digitsOnly.length === 11 && digitsOnly.startsWith("069")) {
    return `30${digitsOnly.slice(1)}`;
  }

  if (digitsOnly.length === 8 && digitsOnly.startsWith("9")) {
    return `357${digitsOnly}`;
  }

  return normalizedMobile;
};

const normalizeIdentity = (value) => {
  const trimmedValue = value?.trim() || "";

  if (trimmedValue.includes("@")) {
    return trimmedValue.toLowerCase();
  }

  return normalizeMobileIdentity(trimmedValue);
};

const isValidEmail = (identity) => emailRegex.test(identity);

const isValidIdentity = (identity) => {
  if (!identity) {
    return false;
  }

  if (identity.includes("@")) {
    return isValidEmail(identity);
  }

  return identity.replace(/\D/g, "").length > 5;
};

const isImpersonating = Boolean(getStorage().getItem("impersonate"));

const AddUserToClubDrawer = ({
  openDrawer,
  onCloseDrawer,
  selectedClub,
  getClubs,
}) => {
  const { t } = useTranslation();
  const snackBar = useContext(SnackBarContext);
  const [filters, setFilters] = useState({ id: "", identity: "" });
  const [user, setUser] = useState();
  const [identityError, setIdentityError] = useState("");
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();

  useEffect(() => {
    const normalizedIdentity = normalizeIdentity(filters.identity);

    if (isValidEmail(normalizedIdentity)) {
      setIdentityError("");
      resolveUser(normalizedIdentity);
    } else if (
      !normalizedIdentity.includes("@") &&
      normalizedIdentity.length > 5
    ) {
      setIdentityError("");
      resolveUser(normalizedIdentity);
    } else {
      setUser(null);
    }
  }, [filters.identity]);

  const resolveUser = (identity = normalizeIdentity(filters.identity)) => {
    if (identity) {
      axiosGraphQL({
        query: SEARCH_USER_WITH_IDENTITY_SA,
        variables: { identity },
      })
        .then((response) => {
          setUser(
            response?.data?.data?.searchUserWithIdentitySA &&
              response?.data?.data?.searchUserWithIdentitySA[0]
          );
        })
        .catch((error) => {
          errorLog(
            error,
            "AddUserToClubDrawer",
            "resolveUser",
            "SEARCH_USER_WITH_IDENTITY_SA"
          );
          snackBar.setOptions({
            alertMessage: error.message,
            alertSeverity: "error",
          });
          snackBar.setSnackbarOpen(true);
        });
    }
  };

  const searchUser = () => {
    const normalizedIdentity = normalizeIdentity(filters.identity);

    if (!isValidIdentity(normalizedIdentity)) {
      setIdentityError(t("enter_valid_email_or_mobile"));
      snackBar.setOptions({
        alertMessage: t("enter_valid_email_or_mobile"),
        alertSeverity: "error",
      });
      snackBar.setSnackbarOpen(true);
      return;
    }

    setIdentityError("");
    resolveUser(normalizedIdentity);
  };

  const handleSubmit = () => {
    axiosGraphQL({
      query: REGISTER_USER_SA,
      variables: {
        firstName: user?.firstName,
        lastName: user?.lastName,
        mobile: user?.mobile,
        email: user?.email,
        role: USER_ROLES.CLUB_ADMIN,
        clubId: selectedClub?._id,
      },
    })
      .then(async () => {
        snackBar.setOptions({
          alertMessage: t("club_admin_successfully_created"),
          alertSeverity: "success",
        });
        snackBar.setSnackbarOpen(true);
        getClubs();
        onCloseDrawer();
      })
      .catch((error) => {
        errorLog(
          error,
          "AddUserToClubDrawer",
          "handleSubmit",
          "REGISTER_USER_SA"
        );
        snackBar.setOptions({
          alertMessage: error.message,
          alertSeverity: "error",
        });
        snackBar.setSnackbarOpen(true);
      });
  };

  const onChangeFilter = ({ target: { name, value } }) => {
    setIdentityError("");
    setFilters((prevState) => ({ ...prevState, [name]: value }));
  };

  return (
    <Drawer
      anchor="right"
      open={openDrawer}
      onClose={onCloseDrawer}
      PaperProps={getTransactionalDrawerPaperProps(
        "add-user-to-club-drawer-title"
      )}
    >
      <DrawerLayout sx={{ pt: isImpersonating ? 3 : 0 }}>
        <DrawerTitleHeader
          closeLabel={t("close")}
          onClose={onCloseDrawer}
          subtitle={t("add_club_admin_description")}
          title={t("add_club_admin")}
          titleId="add-user-to-club-drawer-title"
        />
        <DrawerBody data-testid="add-user-to-club-drawer-body">
          <DrawerSection
            description={t("find_user_description")}
            title={t("find_user_title")}
            titleId="find-user-section-title"
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextFieldRenderer
                  label={t("email_or_mobile")}
                  onChange={onChangeFilter}
                  name={"identity"}
                  value={filters.identity}
                  error={identityError}
                />
              </Grid>
              <Grid item xs={12}>
                <Button startIcon={<SearchOutlinedIcon />} fullWidth onClick={searchUser}>
                  {t("search")}
                </Button>
              </Grid>
              {user && (
                <Grid item xs={12}>
                  <UserIdentitySummary user={user} />
                </Grid>
              )}
            </Grid>
          </DrawerSection>
        </DrawerBody>
        <DrawerFooter data-testid="add-user-to-club-drawer-footer">
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button variant="outlined" fullWidth onClick={onCloseDrawer}>
              {t("cancel")}
            </Button>
            <Button
              startIcon={<AddOutlinedIcon />}
              onClick={handleSubmit}
              fullWidth
              disabled={!user || !isValidIdentity(normalizeIdentity(filters.identity))}
            >
              {t("add")}
            </Button>
          </Stack>
        </DrawerFooter>
      </DrawerLayout>
    </Drawer>
  );
};

const UserIdentitySummary = ({ user }) => {
  const styles = useStyles();
  const identityDetails = [user?.email, user?.mobile]
    .filter(Boolean)
    .join(" • ");

  return (
    <Stack
      direction={"row"}
      alignItems={"center"}
      className={styles.userIdentityContainer}
    >
      <Grid container spacing={1}>
        <Grid item className={styles.userIdentityName}>
          {[user?.firstName, user?.lastName].filter(Boolean).join(" ")}
        </Grid>
        <Grid item className={styles.userIdentityDetail}>
          {identityDetails}
        </Grid>
      </Grid>
    </Stack>
  );
};

export default AddUserToClubDrawer;
